import {createContext} from "preact";
import {useContext, useEffect, useState} from "preact/hooks";
import PublicService from "../services/PublicService";
import groupBy from "lodash/groupBy";
import isEmpty from "lodash/isEmpty";
import snakeCase from "lodash/snakeCase";
import CartService from "@public/services/CartService";
import {calculateLocation} from "../utils/Event";
import {format, getDaysBetween} from "../utils/Date";
import sortBy from "lodash/sortBy";
import Moment from "moment";

export const COURSE_TYPES = {};
export const LOCATIONS = {};

const EventContext = createContext({
    event: {},
    selectedPeriod: {},
    selectedStartDate: '',
    selectedPeriodOption: {},
    selectedPeriodOptionAddOns: [],
    additionalFeatures:[],
    selectedCourseType: null,
    attendeeQty: 1,
    attendees: [],
    periodOptions: [],
    isLoading: true,
    total: null,
    attendeeValidationErrors: [],
    periods: {},
    courseTypes: [],
    upsertSelectedPeriodOptionAddOns: (addon) => [],
    upsertAttendee: (attendee) => [],
    removeAttendee: (index) => undefined,
    initAttendees: () => undefined,
    validateEvent: () => Boolean,
    isStepValid: (step) => Boolean,
    addToCart: async () => undefined,
    canUserModifyDetails: () => Boolean,
});

export function EventProvider({ productId, children }) {
    const [isLoading, setIsLoading] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState({});
    const [selectedStartDate, setSelectedStartDate] = useState('');
    const [periodOptions, setPeriodOptions] = useState([]);
    const [selectedPeriodOption, setSelectedPeriodOption] = useState({});
    const [selectedPeriodOptionAddOns, setSelectedPeriodOptionAddOns] = useState([]);
    const [selectedFeatures, setSelectedFeatures] = useState([]);
    const [additionalFeatures, setAdditionalFeatures] = useState([]);
    const [courseTypes, setCourseTypes] = useState([]);
    const [selectedCourseType, setSelectedCourseType] = useState(null);
    const [periods, setPeriods] = useState({});
    const [attendeeQty, setAttendeeQty] = useState(1);
    const [attendees, setAttendees] = useState([]);
    const [event, setEvent] = useState({});
    const [error, setError] = useState(null);
    const [total, setTotal] = useState(null);
    const [attendeeValidationErrors, setAttendeeValidationErrors] = useState([]);

    if (! productId) {
        console.error('[esc-ii]: productId is null. Please check that this is on the product page with a productId passed in as a property.');
        return;
    }

    useEffect(() => {
        PublicService.fetchEventById(productId)
            .then(res => res.json())
            .then(res => setTimeout(() => {
                setEvent(res);
                res.event_periods = res.event_periods.filter(eventPeriod => eventPeriod.location);
                res.event_periods = res.event_periods.map(eventPeriod => {
                    const _snakeCaseLocation = snakeCase(eventPeriod.location);
                    const { location, actual_location } = calculateLocation(eventPeriod.location);
                    COURSE_TYPES[_snakeCaseLocation] = location;
                    LOCATIONS[_snakeCaseLocation] = actual_location || location;
                    return {
                        ...eventPeriod,
                        location_snake_case: _snakeCaseLocation,
                        actual_location,
                        location
                    }
                });
                const groupedPeriods = groupBy(res.event_periods, 'location_snake_case');
                setPeriods(groupedPeriods);
                for (const groupedPeriod in groupedPeriods) {
                    setCourseTypes(prevState => [...prevState, groupedPeriod]);
                }
                if (Object.keys(groupedPeriods)[0]) {
                    setSelectedCourseType(Object.keys(groupedPeriods)[0])
                    if (groupedPeriods[Object.keys(groupedPeriods)[0]][0])
                        setSelectedPeriod(groupedPeriods[Object.keys(groupedPeriods)[0]][0]);
                }
            }, 500))
            .catch(err => console.error(err) && setError(err))
            .finally(() => setIsLoading(false));
    }, []);

    useEffect(() => {
        if (Array.isArray(selectedPeriod.event_period_options)) {
            let options = selectedPeriod.event_period_options.map(
                option => { return {
                    ...option,
                    value: option.id,
                    label: calcPeriodOptionLabel(option),
                    disabled: ! option.has_quantity_available,
                    price: option.price,
                    daysBetween: getDaysBetween(option.start_date, option.end_date),
                }}
            );
            options = sortBy(options, 'daysBetween');
            setSelectedPeriodOption(options.find(option => ! option.disabled));
            setPeriodOptions(options);
        }
    }, [selectedPeriod]);

    useEffect(() => {
        if (selectedPeriodOptionAddOns.length !== 0) {
            const addons = selectedPeriodOptionAddOns.filter(addon => selectedPeriodOption.id === addon.event_period_option_id);
            if (addons.length === 0) {
                setSelectedPeriodOptionAddOns([]);
            }
        }
        if(selectedPeriodOption.id){
            PublicService.fetchAdditionalFeatures(selectedPeriodOption.id).then(res => res.json())
                .then(res => setTimeout(() => {
                    setAdditionalFeatures(res);
                    setSelectedFeatures([]);
                }, 500))
                .catch(err => console.error(err) && setError(err))
                .finally(() => setIsLoading(false))
        }
    }, [selectedPeriodOption]);

    useEffect(() => {
        const _variant = getVariant();
        if (attendeeQty >= 0 && Object.keys(_variant).length !== 0) {
            setTotal(((attendeeQty * parseFloat(_variant.price)) * 100));
        }
    }, [attendeeQty, selectedPeriodOption, selectedPeriodOptionAddOns])

    const calcPeriodOptionLabel = (option) => {
        let label = option.option_name;

        // if (option.start_date && option.end_date) {
        //     const daysBetween = getDaysBetween(option.start_date, option.end_date);
        //     switch (daysBetween) {
        //         case 0:
        //             label = format(option.start_date, 'dddd');
        //             break;
        //         case 1:
        //             const isStartDateOnSat = format(option.start_date, 'dddd') === 'Saturday';
        //             const isEndDateOnSun = format(option.end_date, 'dddd') === 'Sunday';
        //             label = isStartDateOnSat && isEndDateOnSun ? 'Full Weekend' : 'Days 1-2';
        //             break;
        //         default:
        //             label = `Days 1 - ${daysBetween + 1}`;
        //     }
        // }

        return label;
    };

    const validateDatesLength = () => {
        return ! (isEmpty(selectedPeriod) || isEmpty(selectedPeriodOption) || isEmpty(selectedStartDate));
    }

    const canUserModifyDetails = () => {

        if (Object.keys(selectedPeriod).length === 0) {
            return false;
        }

        return true;
    }

    const validateAttendeeDetails = () => {
        let validationErrors = [];
        let emailRegex = /\S+@\S+\.\S+/;
        let occurrences = [];

        const isYoungProgramme = window.location.pathname.includes('young-');
        const isFutureProgramme = window.location.pathname.includes('future-');

        for (let i = 0; i < attendees.length; i++) {
            if (! attendees[i].first_name || attendees[i].first_name.trim() === '') {
                validationErrors[i] = {
                    ...validationErrors[i],
                    first_name: 'First name is a required field.'
                };
            }

            if (! attendees[i].last_name || attendees[i].last_name.trim() === '') {
                validationErrors[i] = {
                    ...validationErrors[i],
                    last_name: 'Last name is a required field.'
                };
            }

            if (! attendees[i].email || attendees[i].email.trim() === '') {
                validationErrors[i] = {
                    ...validationErrors[i],
                    email: 'Email is a required field.'
                };
            } else if (! emailRegex.test(attendees[i].email)) {
                validationErrors[i] = {
                    ...validationErrors[i],
                    email: 'Email address is incorrect.'
                };
            }
            if (! attendees[i] || attendees[i].parent_guardian_email.trim() === '') {
                validationErrors[i] = {
                    ...validationErrors[i],
                    parent_guardian_email: 'Parent / guardian email is a required field.'
                };
            } else if (! emailRegex.test(attendees[i].parent_guardian_email)) {
                validationErrors[i] = {
                    ...validationErrors[i],
                    parent_guardian_email: 'Email address is incorrect.'
                };
            }

            if (Object.keys(attendees[i].school).length === 0) {
                validationErrors[i] = {
                    ...validationErrors[i],
                    school: 'School is a required field.'
                };
            }

            if (!attendees[i].gender || attendees[i].gender.trim() === '') {
                validationErrors[i] = {
                    ...validationErrors[i],
                    gender: 'Gender is a required field.'
                };
            } else if (!['M', 'F', 'N/A'].includes(attendees[i].gender)) {
                validationErrors[i] = {
                    ...validationErrors[i],
                    gender: 'Gender is invalid.'
                };
            }

            if (!attendees[i].nationality || attendees[i].nationality.trim() === '') {
                validationErrors[i] = {
                    ...validationErrors[i],
                    nationality: 'Nationality is a required field.'
                };
            }

            if (!attendees[i].dob || attendees[i].dob.trim() === '') {
                validationErrors[i] = {
                    ...validationErrors[i],
                    dob: 'Date of birth is a required field.'
                };
            } else {
                const pattern =/^([0-9]{4})-([0-9]{2})-([0-9]{2})$/;
                if (!pattern.test(attendees[i].dob)) {
                    validationErrors[i] = {
                        ...validationErrors[i],
                        dob: 'Date of birth is invalid.'
                    };
                }

                if (isYoungProgramme || isFutureProgramme) {
                    const allowedAgeRanges = {
                        young: [15, 16, 17, 18],
                        future: [12, 13, 14],
                    };

                    const dateOfBirth = Moment(attendees[i].dob);
                    const eventDate = Moment(isYoungProgramme ? selectedPeriodOption.start_date : selectedPeriodOption.end_date);
                    const ageAtEventDate = eventDate.diff(dateOfBirth, 'years');

                    if (!allowedAgeRanges[isYoungProgramme ? 'young' : 'future'].includes(ageAtEventDate)) {
                        validationErrors[i] = {
                            ...validationErrors[i],
                            dob: 'The entered date of birth doesn\'t meet the age requirements for this programme. Our \'Young\' programmes are for students aged 15-18, and \'Future\' programmes are for those aged 12-14. You must be within these age brackets at the time of the programme.'
                        };
                    }
                }
            }

            occurrences.push(
                attendees[i].first_name+
                attendees[i].last_name+
                attendees[i].email+
                attendees[i].parent_guardian_email+
                attendees[i].school.value+
                attendees[i].gender,
                attendees[i].nationality,
                attendees[i].dob
            );
        }

        // TODO: Output in a UX friendly way.
        let uniqueOccurrences = [...new Set(occurrences)];
        if (uniqueOccurrences.length < attendees.length) {
            validationErrors.push({ ref: 'global', message: 'Each attendee must have different details' });
        }

        setAttendeeValidationErrors(validationErrors);

        return validationErrors.length === 0;
    }

    const isStepValid = (step) => {

        if (step.name === 'dates_length') {
            return validateDatesLength();
        }

        if (step.name === 'attendee_details') {
            return validateDatesLength();
        }

        return false;
    }

    const initAttendees = (customer) => {
        const variant = getVariant();
        const period = selectedPeriod;
        const option = selectedPeriodOption;
        if (! variant || ! period || ! option || attendeeQty <= 0) {
            return;
        }

        let accomMealsBreakdown = 0;
        let airportBreakdown = 0;
        let hasPickup = false;
        let hasDropoff = false;

        for (let addon of variant.addons) {
            for (let optionAddon of option.assigned_addons_unique) {
                if (addon.id == optionAddon.addon_id) {
                    let totalWithThisAddon = attendeeQty;
                    for (let attendee of attendees) {
                        if (attendee.option.id == option.id) {
                            for (let attendeeAddon of variant.addons) {
                                if (attendeeAddon.id == addon.id) {
                                    totalWithThisAddon++;
                                }
                            }
                        }
                    }

                    if (parseInt(optionAddon.assigned_addon_quantity_available) <= 0 ) {
                        alert(addon.name + ' for ' + option.option_name + ' is sold out.');
                        return;
                    }

                    if (parseInt(optionAddon.assigned_addon_quantity_available) < totalWithThisAddon) {

                        alert('Not enough places left!\n\nYou requested: ' + totalWithThisAddon + ' ' + addon.name + ' for ' + option.option_name + '\n\nYou can have a maximum of ' + (optionAddon.assigned_addon_quantity_available) + '.');

                        return;
                    }

                    if (optionAddon.addon.name.toLowerCase().indexOf('accom') != -1 || optionAddon.addon.name.toLowerCase().indexOf('meal') != -1) {
                        accomMealsBreakdown = parseFloat(accomMealsBreakdown) + parseFloat(optionAddon.price);

                    } else if (optionAddon.addon.name.toLowerCase().indexOf('pick') != -1) {
                        hasPickup = true;
                    } else if (optionAddon.addon.name.toLowerCase().indexOf('drop') != -1) {
                        hasDropoff = true;
                    }

                    if (optionAddon.addon.name.toLowerCase().indexOf('air') != -1) {
                        airportBreakdown =  parseFloat(airportBreakdown) + parseFloat(optionAddon.price);
                    }
                    break;
                }
            }
        }
        let structuredAttendees = [];
        for (let i = 0; i < attendeeQty; i++) {
            let extras = [];

            for (let addon of variant.addons) {
                if (addon.name.toLowerCase().indexOf('pick') == -1 && addon.name.toLowerCase().indexOf('drop') == -1) {
                    extras.push(addon.name);
                }
            }
            const attendeeExists = attendees.findIndex(attendee => attendee.index === i);
            const data = {
                index: i,
                variant_id: variant.shopify_variant_id,
                variant_price: variant.price,
                period: period.date_range,
                title: variant.title,
                first_name: '',
                last_name: '',
                gender: '',
                nationality: '',
                email: '',
                parent_guardian_email: '',
                school: {
                    value: '',
                    label: '',
                },
                accom_meals_price: accomMealsBreakdown,
                airport_price: airportBreakdown,
                option: option,
                variant: variant,
                hasPickup: hasPickup,
                hasDropoff: hasDropoff,
                extras: extras.length > 0 ? extras.join(', ') : 'None',
                dob: '',
            };

            if (attendeeExists !== -1) {
                structuredAttendees.push({
                    ...attendees[attendeeExists],
                    ...data
                })
                continue;
            }

            structuredAttendees.push(data);
        }
        setAttendees(structuredAttendees);
    }

    const validateEvent = () => {
        return false;
    }

    const canHaveMultipleAttendees = () => {
        if (!selectedPeriod) {
            return false;
        }

        return ['INTERNSHIP'].includes(selectedPeriod.type);
    }


    const upsertSelectedPeriodOptionAddOns = (addon) => {
        if (Array.isArray(addon)) {
            return setSelectedPeriodOptionAddOns(addon);
        }

        const exists = selectedPeriodOptionAddOns.findIndex(
            selectedAddon => selectedAddon.event_period_addon_id === addon.event_period_addon_id
        );

        if (exists !== -1) {
            selectedPeriodOptionAddOns.splice(exists, 1);
        } else {
            selectedPeriodOptionAddOns.push(addon);
        }

        setSelectedPeriodOptionAddOns([...selectedPeriodOptionAddOns]);
        return selectedPeriodOptionAddOns;
    }

    const upsertAttendee = (attendee) => {
        if (Array.isArray(attendee)) {
            return setAttendees(attendee);
        }

        const exists = attendees.findIndex(
            selectedAttendee => selectedAttendee.index === attendee.index
        );

        if (exists !== -1) {
            attendees[exists] = {...attendees[exists], ...attendee };
        } else {
            attendees.push(attendee);
        }
        setAttendees([...attendees]);
        return attendees;
    }

    const removeAttendee = (index) => {
        attendees.splice(index, 1);
    }

    const addToCart = async (redirectTo = '/cart', deposit = false) => {
        if (! validateAttendeeDetails()) {
            return;
        }

        const cart = (await CartService.getCart()).data;

        for (let item of cart.items) {
            let update = {};
            update[item.key] = 0;

            if (item.properties && item.properties.event_id == event.id) {
                // remove same event
                await CartService.updateCart({updates: update});
            }
        }

        // add to cart
        for (let attendee of attendees) {
            let properties = {
                first_name: attendee.first_name,
                last_name: attendee.last_name,
                email: attendee.email,
                parent_guardian_email: attendee.parent_guardian_email,
                school_id: attendee.school.value,
                school: attendee.school.label,
                accom_meals_price: parseFloat(attendee.accom_meals_price).toFixed(2),
                airport_price: parseFloat(attendee.airport_price).toFixed(2),
                start_date: selectedPeriodOption.start_date,
                end_date: selectedPeriodOption.end_date,
                event_id: event.id,
                nationality: attendee.nationality,
                dob: attendee.dob,
                gender: attendee.gender,
                _type: selectedPeriod.type,
                _is_member: false,
                _base_option_price: getBasePrice()
            };

            if (deposit) {
                properties._deposit = true;
                properties._original_amount_due = attendee.variant_price;
            }

            await CartService.addLineItem({
                id: attendee.variant_id,
                quantity: 1,
                properties: properties
            });
        }

        // redirect to cart
        window.location.href = redirectTo;
    }

    const getVariant = () => {
        if (! selectedPeriodOption || ! Array.isArray(selectedPeriodOption.event_period_variants)) {
            return false;
        }

        const variants = selectedPeriodOption.event_period_variants
            .filter(variant => variant.addons.length === selectedPeriodOptionAddOns.length);

        return variants.find(variant => {
            const variants = variant.addons.filter(addon => {
                return selectedPeriodOptionAddOns.find(selectedAddOn => selectedAddOn.addon_id === addon.id);
            })

            return variants.length === selectedPeriodOptionAddOns.length;
        });
    }

    const getBasePrice = () => {
        const variant = getVariant();
        if (variant.title.endsWith('with no addons')) {
            console.log('[InvestIN@getBasePrice] using original variant', variant);
            return variant.price;
        }

        const variantPreAddonTitle = variant.title.includes('with') ? variant.title.split(' with')[0] : null;

        const noAddonVariants = selectedPeriodOption.event_period_variants.filter((variant) => {
            let endsWith = variant.title.endsWith('with no addons');
            let startsWith = true;

            if (variantPreAddonTitle) {
                startsWith = variant.title.startsWith(variantPreAddonTitle);
            }

            return endsWith && startsWith;
        });

        if (!noAddonVariants.length) {
            console.log('[InvestIN@getBasePrice] no addon variants found, using original variant');
            return variant.price;
        }

        console.log('[InvestIN@getBasePrice] using addon variant', noAddonVariants[0]);

        return noAddonVariants[0].price;
    }

    return (
        <EventContext.Provider value={{
            selectedCourseType,
            setSelectedCourseType,
            courseTypes,
            periods,
            selectedPeriod,
            selectedStartDate,
            selectedPeriodOption,
            setSelectedPeriodOption,
            additionalFeatures,
            selectedPeriodOptionAddOns,
            periodOptions,
            setPeriodOptions,
            upsertSelectedPeriodOptionAddOns,
            upsertAttendee,
            removeAttendee,
            isLoading,
            setIsLoading,
            setSelectedPeriod,
            setSelectedStartDate,
            attendeeQty,
            setAttendeeQty,
            attendees,
            setAttendees,
            event,
            setEvent,
            canHaveMultipleAttendees,
            getVariant,
            initAttendees,
            validateEvent,
            isStepValid,
            addToCart,
            total,
            attendeeValidationErrors,
            canUserModifyDetails,
        }}>
            {children}
        </EventContext.Provider>
    )
}

export function useEventContext() {
    return useContext(EventContext);
}

export default EventContext;
